<template>
  <div id="pickup-checkout">
    <div class="container">
      <div class="row center-xs" v-if="isAnywhereUnavailable">
        <div class="col-md-8 col-xs-12 pb70 pt20 align-center">
          <h2>{{ $t('Anywhere delivery is currently unavailable in your city') }}</h2>
          <p>
            {{ $t('Please check back later, or') }}
            <router-link :to="localizedRoute(`/${locationCode}`)" class="cl-secondary no-underline">
              {{ $t('browse our catalog') }}
            </router-link>.
          </p>
        </div>
      </div>
      <div class="row center-xs" v-else-if="!isThankYouPage">
        <div class="col-md-8 col-xs-12 pb70 pt20">
          <what-need-to-pickup class="relative" :is-active="activeSection.whatNeedToPickup" />
          <personal-details class="relative"
                            :is-active="activeSection.personalDetails"
                            :focused-field="'email'"
          />
          <pickup-address class="relative" :is-active="activeSection.pickupAddress" />
          <shipping class="relative" :is-active="activeSection.shipping"/>
          <delivery-time class="relative" :is-active="activeSection.deliveryTime" />
          <payment class="relative" :is-active="activeSection.payment" />
          <order-review class="relative"
                        :is-active="activeSection.orderReview"
                        :user-info="personalDetails"
                        :shipping="shipping"
                        :pickup-address="pickupAddress"
                        :pickup-target="whatNeedToPickup"
                        :delivery-time="deliveryTime"
                        :payment="payment"
          />
        </div>
      </div>
      <thank-you-page v-if="isThankYouPage" />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex'
import PersonalDetails from 'theme/components/theme/blocks/PickupCheckout/PersonalDetails'
import WhatNeedToPickup from 'theme/components/theme/blocks/PickupCheckout/WhatNeedToPickup'
import PickupAddress from 'theme/components/theme/blocks/PickupCheckout/PickupAddress'
import Shipping from 'theme/components/theme/blocks/PickupCheckout/Shipping'
import Payment from 'theme/components/theme/blocks/PickupCheckout/Payment'
import OrderReview from 'theme/components/theme/blocks/PickupCheckout/OrderReview'
import ThankYouPage from "../components/theme/blocks/PickupCheckout/ThankYouPage";
import DeliveryTime from 'theme/components/theme/blocks/PickupCheckout/DeliveryTime'
import { isServer } from '@vue-storefront/core/helpers'
import moment from 'moment'
import config from "config";
import { Logger } from '@vue-storefront/core/lib/logger';
import { handleOrderProgress } from '@vue-storefront/core/modules/order/helpers/orderProgress'

export default {
  name: 'Anywhere',
  components: {
    PersonalDetails,
    WhatNeedToPickup,
    PickupAddress,
    Shipping,
    Payment,
    DeliveryTime,
    OrderReview,
    ThankYouPage
  },
  data () {
    return {
      activeSection: {
        whatNeedToPickup: true,
        personalDetails: false,
        pickupAddress: false,
        shipping: false,
        deliveryTime: false,
        payment: false,
        orderReview: false
      },
      personalDetails: {},
      whatNeedToPickup: '',
      pickupAddress: {},
      shipping: {},
      deliveryTime: {
        datetime: '',
        asap: true
      },
      payment: {},
      orderReview: {},
      validationResults: {
        personalDetails: { $invalid: true },
        pickupAddress: { $invalid: true },
        shipping: { $invalid: true },
      },
      locationCode: '',
      partnerCode: 'anywhere'
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.user.current
    }),
    ...mapGetters({
      currentPartner: 'partners/getCurrentPartner',
      isThankYouPage: 'pickup-checkout/isThankYouPage',
      metaInformation: 'configs/getMetaInformation',
    }),
    ogMetaType () {
      if (this.metaInformation.find(meta => meta.key === 'og_type')) return this.metaInformation.find(meta => meta.key === 'og_type').value
      return ''
    },
    ogMetaTitle () {
      if (this.metaInformation.find(meta => meta.key === 'og_title')) return this.metaInformation.find(meta => meta.key === 'og_title').value
      return ''
    },
    ogMetaDescription () {
      if (this.metaInformation.find(meta => meta.key === 'og_description')) return this.metaInformation.find(meta => meta.key === 'og_description').value
      return ''
    },
    ogMetaImage () {
      if (this.metaInformation.find(meta => meta.key === 'og_image')) return this.metaInformation.find(meta => meta.key === 'og_image').value
      return ''
    },
    getHostPath () {
      return config.canonical ? config.canonical.url : ''
    },
    // K2-2817: the per-city `anywhere` partner's `active` flag is the on/off switch for
    // the "deliver anything" feature (its own "closed" toggle, K2-3026 semantics). Show
    // an unavailable state instead of the checkout flow when it's off. Only acts on a
    // confirmed-loaded partner record - `currentPartner` is `false` while asyncData's
    // loadOnePartner is still in flight, and a transient load failure must fall through
    // to the normal flow, not a false "unavailable". Deliberately checks `active` only,
    // not `enabled` (`anywhere` is `enabled: true` but excluded from listings by code -
    // see the partners store getters).
    isAnywhereUnavailable () {
      return !!this.currentPartner && this.currentPartner.active === false
    }
  },
  created () {
    this.locationCode = this.$route.params.locationCode
  },
  mounted() {
    this.changeCurrentPartner(this.partnerCode)
  },
  async asyncData ({ store, route, context }) {
    let partnerCode = 'anywhere'
    try {
      await store.dispatch('partners/loadOnePartner', { code: partnerCode, locationCode: route.params.locationCode }).then(() =>
        store.dispatch('partners/changeCurrentPartner', partnerCode))
    } catch (error) {
      Logger.error(error, 'sync')();
    }
  },

  async beforeRouteEnter (to, from, next) {
    next(async vm => {
      // K2-3036: use the fixed 'anywhere' code, matching every other hook in this file -
      // this used to pass to.name (a route name, not a partner code).
      const partnerCode = 'anywhere'
      if (!vm.$store.getters['partners/getPartnerByCode'](partnerCode)) {
        try {
          await vm.$store.dispatch('partners/loadOnePartner', { code: partnerCode, locationCode: to.params.locationCode }).then(() =>
            vm.$store.dispatch('partners/changeCurrentPartner', partnerCode))
        } catch (error) {
          Logger.error(error, 'sync')();
        }
      }
    })
  },

  metaInfo () {
    let text = ''
    if (this.currentPartner) {
      if (this.currentPartner.hasOwnProperty('meta_title') && this.currentPartner.meta_title) {
        text = this.currentPartner.meta_title
      } else {
        text = this.currentPartner.display_mode === 'food'
            ? `Доставка до 60 хвилин з ${this.currentPartner.title}  - Зручне замовлення онлайн.`
            : `${this.currentPartner.about} - Доставка якнайшвидше або у вибраний час`
      }
      let currentParentMeta = [
        {
          vmid: 'description',
          name: 'description',
          content: this.currentPartner.meta_description ? this.currentPartner.meta_description
              : 'Привеземо Вам усе що забажаєте. У нас в каталозі Ви можете переглянути та замовити страви та товари з найкращих закладів харчування, супермаркетів та гіпермаркетів міста'
        },
        {
          vmid: 'keywords',
          name: 'keywords',
          content: this.currentPartner.meta_keywords || this.currentPartner.meta_keywords
        },
        {
          vmid: 'og:type',
          property: 'og:type',
          content: this.currentPartner.og_meta_type ? this.currentPartner.og_meta_type : this.ogMetaType
        },
        {
          vmid: 'og:title',
          property: 'og:title',
          content: this.currentPartner.og_meta_title ? this.currentPartner.og_meta_title : this.ogMetaTitle
        },
        {
          vmid: 'og:description',
          property: 'og:description',
          content: this.currentPartner.og_meta_description ? this.currentPartner.og_meta_description : this.ogMetaDescription
        },
        {
          vmid: 'og:image',
          property: 'og:image',
          content: this.currentPartner.og_meta_image ? this.currentPartner.og_meta_image : this.ogMetaImage
        }
      ]
      return {
        title: text,
        meta: currentParentMeta,
        link: [
          {rel: 'canonical', href: this.getHostPath + this.localizedRoute(this.$route.fullPath)}
        ]
      }
    }
  },
  methods: {
    ...mapActions({
      changeCurrentPartner: 'partners/changeCurrentPartner'
    }),
    onAfterPlaceOrder (payload) {
      // K2-3073: stops the order's loader (no online payment here).
      return handleOrderProgress(this.$bus, payload, async () => {
        this.confirmation = payload.confirmation;
        await this.$store.dispatch('pickup-checkout/setThankYouPage', true);
      })
    },
    onBeforeEdit (section) {
      this.activateSection(section)
    },
    onDoPlaceOrder () {
      this.checkConnection({ online: typeof navigator !== 'undefined' ? navigator.onLine : true })
      this.$store.dispatch('pickup-checkout/placeOrder', { order: this.prepareOrder() })
    },
    onAfterPersonalDetails (receivedData, validationResult) {
      this.personalDetails = receivedData
      this.validationResults.personalDetails = validationResult

      this.activateSection('pickupAddress')
      this.focusedField = null
    },
    onAfterPickupAddress (receivedData, validationResult) {
      this.pickupAddress = receivedData
      this.validationResults.pickupAddress = validationResult

      this.activateSection('shipping')
    },
    onAfterShippingDetails (receivedData, validationResult) {
      this.shipping = receivedData
      this.validationResults.shipping = validationResult
      this.activateSection('deliveryTime')
    },
    onAfterWhatNeedToPickup (receivedData, validationResult) {
      this.whatNeedToPickup = receivedData
      this.validationResults.personalDetails = validationResult

      this.activateSection('personalDetails')
    },
    onAfterDeliveryTimeDetails (receivedData, validationResult, isAsap) {
      this.deliveryTime.datetime = receivedData
      this.deliveryTime.asap = isAsap
      this.validationResults.deliveryTime = validationResult
      this.activateSection('payment')
    },
    onAfterPaymentDetails (receivedData, validationResult) {
      this.payment = receivedData
      this.activateSection('orderReview')
    },
    onAfterOrderReview (receivedData, validationResult) {
      this.orderReview = receivedData
    },
    onNetworkStatusCheck (isOnline) {
      this.checkConnection(isOnline)
    },
    activateHashSection () {
      if (!isServer) {
        var urlStep = window.location.hash.replace('#', '')
        if (this.activeSection.hasOwnProperty(urlStep) && this.activeSection[urlStep] === false) {
          this.activateSection(urlStep)
        } else if (urlStep === '') {
          this.activateSection('personalDetails')
        }
      }
    },
    checkConnection (isOnline) {
      if (!isOnline) {
        this.notifyNoConnection()
      }
    },
    activateSection (sectionToActivate) {
      for (let section in this.activeSection) {
        this.activeSection[section] = false
      }
      this.activeSection[sectionToActivate] = true
      if (!isServer) window.location.href = window.location.origin + window.location.pathname + '#' + sectionToActivate
    },
    prepareOrder () {
      let dateAndTime = this.deliveryTime.datetime
        .split(/(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2} — \d{2}:\d{2})/);
      let deliveryTime = {
        delivery_time: dateAndTime[2],
        delivery_date: moment(dateAndTime[1]).format('DD.MM.YYYY')
      };
      let loginUserPhone = this.$store.state.user.current ? this.$store.state.user.current.telephone : ''
      this.order = {
        user_id: this.$store.state.user.current ? this.$store.state.user.current.id.toString() : '',
        customer_id: this.$store.state.user.current ? this.$store.state.user.current.id.toString() : '',
        partner_code: this.partnerCode,
        location_code: this.locationCode,
        comments: [{
          partner: this.partnerCode,
          comment: this.whatNeedToPickup,
        }],
        is_asap: this.deliveryTime.asap,
        contactToReplace: this.orderReview.contactToReplace,
        addressInformation: {
          billingAddress: {
            state: this.shipping.state,
            country: this.shipping.country,
            street: this.shipping.street,
            house: this.shipping.house,
            apartment: this.shipping.apartment,
            company: this.payment.company,
            phone_number: this.shipping.phone_number ? this.shipping.phone_number : this.personalDetails.phone_number,
            postcode: this.shipping.postcode ? this.shipping.postcode : this.shipping.zipCode,
            city: this.shipping.city,
            firstname: this.shipping.firstName ? this.shipping.firstName : this.personalDetails.firstName,
            lastname: this.shipping.lastName ? this.shipping.lastName : this.personalDetails.lastName,
            email: this.personalDetails.emailAddress,
            vat_id: this.payment.taxId,
            comment: this.shipping.comment ? this.shipping.comment : '',
            longitude: this.shipping.longitude ? this.shipping.longitude : '',
            latitude: this.shipping.latitude ? this.shipping.latitude : '',
            api_details: this.shipping.api_details ? this.shipping.api_details : '',
            add_new_address: this.$store.state.checkout.addNewAddress,
            default_shipping: this.shipping.default_shipping
          },
          shipping_method_code: this.partnerCode,
          shipping_carrier_code: this.partnerCode,
          payment_method_code: this.payment.code,
        },
        items: [{
          name: 'Доставка звідусіль',
          sku: 'ANY',
          ean: '000000000',
          product_id: '0',
          image: 'http://',
          qty: 1,
          final_price: 0,
          totals: {
            base_price: 0,
            row_total_incl_tax: 0,
            tax_amount: 0,
            tax_percent: 0,
            discount_amount: 0,
            row_total: 0
          }
        }]
      }
      this.order.addressInformation.shippingAddress = {
        state: this.shipping.state,
        street: this.shipping.street,
        house: this.shipping.house,
        apartment: this.shipping.apartment,
        country: this.shipping.country,
        company: this.payment.company,
        phone_number: this.shipping.phone_number ? this.shipping.phone_number : this.personalDetails.phone_number,
        postcode: this.shipping.postcode ? this.shipping.postcode : this.shipping.zipCode,
        city: this.shipping.city,
        firstname: this.shipping.firstName ? this.shipping.firstName : this.personalDetails.firstName,
        lastname: this.shipping.lastName ? this.shipping.lastName : this.personalDetails.lastName,
        telephone: this.shipping.telephone ? this.shipping.telephone : loginUserPhone,
        comment: this.shipping.comment ? this.shipping.comment : '',
        longitude: this.shipping.longitude ? this.shipping.longitude : '',
        latitude: this.shipping.latitude ? this.shipping.latitude : '',
        api_details: this.shipping.api_details ? this.shipping.api_details : '',
      }
      this.order.deliveryTime = `${deliveryTime.delivery_date.split(".").reverse().join("-")} ${deliveryTime.delivery_time}`
      this.order.pickupAddress = {
        street: this.pickupAddress.street,
        house: this.pickupAddress.house,
        apartment: this.pickupAddress.apartment,
        city: this.pickupAddress.city,
        state: this.pickupAddress.state,
        country: this.pickupAddress.country,
        postcode: this.pickupAddress.postcode,
        longitude: this.pickupAddress.longitude,
        latitude: this.pickupAddress.latitude,
        api_details: this.pickupAddress.api_details,
        comment: this.pickupAddress.comment
      }

      return this.order
    },
    focusField (fieldName) {
      if (fieldName === 'password') {
        window.scrollTo(0, 0)
        this.activateSection('personalDetails')
        this.focusedField = fieldName
      }
      if (fieldName === 'email-address') {
        window.scrollTo(0, 0)
        this.activateSection('personalDetails')
        this.focusedField = fieldName
      }
    }
  },
  beforeMount () {
    this.$bus.$on('pickup-checkout-after-authentication', this.onAfterAuthentication)
    this.$bus.$on('pickup-checkout-after-personalDetails', this.onAfterPersonalDetails)
    this.$bus.$on('pickup-checkout-after-whatNeedToPickup', this.onAfterWhatNeedToPickup)
    this.$bus.$on('pickup-checkout-after-pickupAddress', this.onAfterPickupAddress)
    this.$bus.$on('pickup-checkout-after-shippingToDetails', this.onAfterShippingDetails)
    this.$bus.$on('pickup-checkout-after-deliveryTimeDetails', this.onAfterDeliveryTimeDetails)
    this.$bus.$on('pickup-checkout-after-paymentDetails', this.onAfterPaymentDetails)
    this.$bus.$on('pickup-checkout-after-orderReview', this.onAfterOrderReview)
    this.$bus.$on('pickup-checkout-do-placeOrder', this.onDoPlaceOrder)
    this.$bus.$on('pickup-checkout-before-edit', this.onBeforeEdit)
    this.$bus.$on('order-after-placed', this.onAfterPlaceOrder)
    this.$bus.$on('pickup-checkout-after-shippingMethodChanged', this.onAfterShippingMethodChanged)
    this.$bus.$on('pickup-checkout-after-validationError', this.focusField)
  },
  beforeDestroy () {
    this.$bus.$off('pickup-checkout-after-authentication', this.onAfterAuthentication)
    this.$bus.$off('pickup-checkout-after-personalDetails', this.onAfterPersonalDetails)
    this.$bus.$off('pickup-checkout-after-whatNeedToPickup', this.onAfterWhatNeedToPickup)
    this.$bus.$off('pickup-checkout-after-pickupAddress', this.onAfterPickupAddress)
    this.$bus.$off('pickup-checkout-after-shippingToDetails', this.onAfterShippingDetails)
    this.$bus.$off('pickup-checkout-after-deliveryTimeDetails', this.onAfterDeliveryTimeDetails)
    this.$bus.$off('pickup-checkout-after-paymentDetails', this.onAfterPaymentDetails)
    this.$bus.$off('pickup-checkout-after-orderReview', this.onAfterOrderReview)
    this.$bus.$off('pickup-checkout-do-placeOrder', this.onDoPlaceOrder)
    this.$bus.$off('pickup-checkout-before-edit', this.onBeforeEdit)
    this.$bus.$off('order-after-placed', this.onAfterPlaceOrder)
    this.$bus.$off('pickup-checkout-after-shippingMethodChanged', this.onAfterShippingMethodChanged)
    this.$bus.$off('pickup-checkout-after-validationError', this.focusField)
  },
  deactivated () {
    this.activeSection = {
      whatNeedToPickup: true,
      personalDetails: false,
      pickupAddress: false,
      shipping: false,
      deliveryTime: false,
      payment: false,
      orderReview: false
    }
    this.$router.replace({ hash: '' });
  }
}
</script>
<style lang="scss">
  #pickup-checkout {
    .number-circle {
      width: 35px;
      height: 35px;
      background-color: #11bb2e;

      @media (max-width: 768px) {
        width: 28px;
        height: 28px;
        line-height: 28px;
      }
    }
    h3{
      font-weight: 300;
      color: #333333;
      line-height: 35px;
      @media (max-width: 768px) {
        line-height: 28px;
        font-size: 16px;
      }
    }
    button{
      color: #fff;
      border: none;
      padding-top: 9px;
      padding-bottom: 9px;
      height: 46px;
      border-radius: 10px;
      background-color: #11bb2e;
      align-items: center;
      justify-content: center;
      text-align: center;
      display: flex;
      @media (max-width: 768px) {
        font-size: 12px;
      }
    }
    i.arrow-i{
      transform: rotate(90deg);
      &.active{
        transform: rotate(-90deg);
      }
    }
    input {
      &:not(.country-selector__input) {
        border: 1px solid #bdbdbd;
        border-radius: 5px;
        padding-left: 10px;
        padding-top: 15px;
      }
    }
    .base-input{
      label{
        font-size: 12px;
        color: #111;
        opacity: 0.5;
        left: 10px;
        top: 15px;
      }
      input:focus ~ label, input:not(.empty) ~ label{
        top: 1px;
      }
    }
    .base-input-additional-text {
      label {
        font-size: 12px;
      }
    }
    textarea:focus ~ label,
    textarea:not(.empty) ~ label {
      top: 0px;
      font-size: 12px;
      color: #9a9a9a;
    }
  }
</style>

<style lang="scss" scoped>
  @import '~theme/css/base/text';
  @import '~theme/css/variables/colors';
  @import '~theme/css/helpers/functions/color';
  $bg-secondary: color(secondary, $colors-background);
  $color-tertiary: color(tertiary);
  $color-secondary: color(secondary);
  $color-error: color(error);
  $color-white: color(white);
  $color-black: color(black);

  #pickup-checkout {
    .number-circle {
      width: 35px;
      height: 35px;

      @media (max-width: 768px) {
        width: 23px;
        height: 23px;
        line-height: 23px;
      }
    }
    .radioStyled {
      display: block;
      position: relative;
      padding-left: 35px;
      margin-bottom: 12px;
      cursor: pointer;
      font-size: 16px;
      line-height: 25px;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;

      input {
        position: absolute;
        opacity: 0;
        cursor: pointer;
      }

      .checkmark {
        position: absolute;
        top: 0;
        left: 0;
        height: 20px;
        width: 20px;
        border-radius: 50%;
        border: 1px solid #f9a000;

        &:after {
          content: "";
          position: absolute;
          display: none;
          top: 2px;
          left: 2px;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: $color-secondary;
        }
      }

      input:checked ~ .checkmark:after {
        display: block;
      }
    }
  }

  .line {
    &:after {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      left: 37px;
      z-index: -1;
      width: 1px;
      height: 100%;
      background-color: $bg-secondary;

      @media (max-width: 768px) {
        display: none;
      }
    }
  }

  .title {
    @media (max-width: 768px) {
      background-color: $bg-secondary;
      margin-bottom: 25px;

      h1 {
        font-size: 36px;
      }
    }
  }
</style>
